import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { Plate } from '../../constants/plates';

interface Props {
    plate: Plate | null;
    onClose: () => void;
}

const scoreColor = (score: number) => {
    if (score >= 75) return '#02F3A2';
    if (score >= 55) return '#FCD903';
    return '#FF6A59';
};

/** Macro tiles — icon + uppercase label + value, as in v1. */
const MacroTile: React.FC<{ icon: string; label: string; value: string }> = ({
    icon,
    label,
    value,
}) => (
    <div className="flex items-center gap-2.5 rounded-2xl border-[0.8px] border-dark-slate bg-dark-gunmetal p-2">
        <img src={icon} alt="" aria-hidden="true" loading="lazy" className="h-8 w-8" />
        <div>
            <p className="text-[9px] font-medium tracking-[0.1em] text-dark-textSubtle">{label}</p>
            <p className="text-sm font-semibold text-white">{value}</p>
        </div>
    </div>
);

/** Secondary nutrient rows below the analysis block. */
const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="flex justify-between gap-4 rounded-xl bg-dark-gunmetal px-3.5 py-3">
        <span className="text-[13px] text-dark-textSecondary">{label}</span>
        <span className="text-[13px] font-semibold text-white">{value}</span>
    </div>
);

const BreakdownSheet: React.FC<Props> = ({ plate, onClose }) => {
    const closeRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!plate) return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeRef.current?.focus();

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [plate, onClose]);

    // Rendered through a portal: the sections that host this sheet are
    // `relative z-[1]`, which creates a stacking context that would trap a
    // fixed-position overlay inside the section instead of covering the page.
    return createPortal(
        <AnimatePresence>
            {plate && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[100] flex items-end justify-center bg-[rgba(8,10,11,0.9)] p-0 backdrop-blur-[12px] sm:items-center sm:p-6"
                    onClick={onClose}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Nutrition breakdown: ${plate.food_item}`}
                >
                    <motion.div
                        initial={{ y: '100%', opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: '100%', opacity: 0 }}
                        transition={{ type: 'spring', damping: 32, stiffness: 320 }}
                        onClick={(e) => e.stopPropagation()}
                        className="flex max-h-[92vh] w-full max-w-[430px] flex-col overflow-hidden rounded-t-3xl bg-dark-base shadow-[0_30px_80px_rgba(0,0,0,0.6)] sm:max-h-[86vh] sm:rounded-3xl lg:max-w-[880px] lg:flex-row"
                    >
                        {/* Photo: a banner on phones, a full-height panel beside
                            the detail once there is room. */}
                        <div className="relative shrink-0 lg:w-[46%]">
                            <img
                                src={plate.image}
                                alt={plate.food_item}
                                className="h-44 w-full object-cover sm:h-56 lg:h-full"
                                style={{ objectPosition: plate.pos }}
                            />
                            <span
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-dark-base lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-dark-base"
                            />
                            <span className="absolute left-4 top-4 rounded-full border border-dark-gunmetal bg-[rgba(15,19,22,0.72)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-dark-textSecondary backdrop-blur-xl">
                                {plate.origin}
                            </span>
                            <button
                                ref={closeRef}
                                onClick={onClose}
                                aria-label="Close breakdown"
                                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white backdrop-blur transition-colors hover:bg-black/80 lg:hidden"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="relative min-w-0 flex-1 overflow-y-auto px-5 pb-8 pt-4 sm:px-7">
                            <button
                                onClick={onClose}
                                aria-label="Close breakdown"
                                className="absolute right-5 top-5 hidden h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white transition-colors hover:bg-black/70 lg:flex"
                            >
                                <X className="h-4 w-4" />
                            </button>
                            {/* score */}
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-dark-textSubtle">
                                    Meal score
                                </span>
                            </div>
                            <p className="mt-1 flex items-baseline gap-1">
                                <span
                                    className="text-[40px] font-extrabold leading-none tracking-[-0.03em]"
                                    style={{ color: scoreColor(plate.score) }}
                                >
                                    {Math.round(plate.score / 10)}
                                </span>
                                <span className="text-base font-medium text-dark-textSecondary">
                                    /10
                                </span>
                            </p>

                            <p className="mt-4 text-[19px] font-bold leading-tight tracking-[-0.02em] text-white">
                                {plate.food_item}
                            </p>
                            <p className="mt-1.5 text-[12px] text-dark-textSecondary">
                                {plate.weight} · {plate.processing_label} · micronutrients{' '}
                                {plate.micronutrient_score}/10
                            </p>

                            {/* macro tiles — three across once the sheet widens */}
                            <div className="mt-5 grid grid-cols-2 gap-2.5 lg:grid-cols-3">
                                <MacroTile
                                    icon="/assets/app/calories.png"
                                    label="CALORIES"
                                    value={plate.calories}
                                />
                                <MacroTile
                                    icon="/assets/app/carbohydrates.png"
                                    label="CARBS"
                                    value={plate.carbohydrates}
                                />
                                <MacroTile
                                    icon="/assets/app/sugar.png"
                                    label="SUGAR"
                                    value={plate.natural_sugar}
                                />
                                <MacroTile
                                    icon="/assets/app/fiber.png"
                                    label="FIBER"
                                    value={plate.fiber}
                                />
                                <MacroTile
                                    icon="/assets/app/protein.png"
                                    label="PROTEIN"
                                    value={plate.protein}
                                />
                                <MacroTile icon="/assets/app/fat.png" label="FAT" value={plate.fat} />
                            </div>

                            {/* analysis */}
                            <div className="mt-7">
                                <p className="text-[10px] font-semibold tracking-[0.14em] text-dark-textSubtle">
                                    MEAL ANALYSIS
                                </p>

                                <div className="mt-3.5 flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-success" />
                                    <span className="text-sm font-semibold text-white">Positives</span>
                                </div>
                                <ul className="mt-2.5 grid gap-2">
                                    {plate.positives.map((p) => (
                                        <li key={p} className="flex gap-2.5">
                                            <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-success" />
                                            <span className="text-[14px] leading-relaxed text-dark-textSecondary">
                                                {p}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-5 flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-[#DDDD61]" />
                                    <span className="text-sm font-semibold text-white">
                                        Considerations
                                    </span>
                                </div>
                                <ul className="mt-2.5 grid gap-2">
                                    {plate.considerations.map((c) => (
                                        <li key={c} className="flex gap-2.5">
                                            <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-[#DDDD61]" />
                                            <span className="text-[14px] leading-relaxed text-dark-textSecondary">
                                                {c}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* detail rows */}
                            <div className="mt-6 grid gap-1.5 sm:grid-cols-2">
                                <DetailRow label="Refined carbs" value={plate.refined_carbs} />
                                <DetailRow label="Added sugar" value={plate.added_sugar} />
                                <DetailRow label="Saturated fat" value={plate.sat_fat} />
                                <DetailRow label="Unsaturated fat" value={plate.unsaturated_fat} />
                                <DetailRow label="Trans fat" value={plate.trans_fat} />
                                <DetailRow label="Sodium" value={plate.sodium} />
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default BreakdownSheet;
